import { useCallback, useState } from "react";
import type { CreateServiceRequest } from "@/types/api";
import { mongoCredentialError } from "./connectionCredentials";
import { type AuthMode, MongoDBFields } from "./MongoDBFields";
import {
  mongoAuthMechanismError,
  withX509Mechanism,
} from "./mongoAuthMechanism";

export function useMongoFields() {
  const [connectionString, setConnectionString] = useState("");
  const [dbName, setDbName] = useState("");
  const [writeMode, setWriteMode] = useState<"insert" | "replace">("insert");
  const [authMode, setAuthMode] = useState<AuthMode>("none");
  const [clientCert, setClientCert] = useState("");
  const [clientKey, setClientKey] = useState("");
  const [caCert, setCaCert] = useState("");

  const toPayload = useCallback((): Partial<CreateServiceRequest> => {
    // Checked on Add (TTTL-84): the throw lands in the error toast and
    // keeps the submit from reaching the server, where the driver would
    // refuse the URI as a 500.
    const uriError =
      mongoCredentialError(connectionString) ??
      mongoAuthMechanismError(connectionString, authMode);
    if (uriError) throw new Error(uriError);
    return {
      // x509 authenticates only when the URI names the mechanism; the
      // certificates alone set up TLS (see withX509Mechanism).
      connection_string:
        authMode === "x509_inline"
          ? withX509Mechanism(connectionString)
          : connectionString,
      db_name: dbName,
      write_mode: writeMode,
      ...(authMode === "x509_inline" && {
        auth: {
          kind: "x509_inline" as const,
          client_cert: clientCert,
          client_key: clientKey,
          ...(caCert ? { ca_cert: caCert } : {}),
        },
      }),
    };
  }, [
    connectionString,
    dbName,
    writeMode,
    authMode,
    clientCert,
    clientKey,
    caCert,
  ]);

  const fields = (
    <MongoDBFields
      connectionString={connectionString}
      setConnectionString={setConnectionString}
      dbName={dbName}
      setDbName={setDbName}
      writeMode={writeMode}
      setWriteMode={setWriteMode}
      authMode={authMode}
      setAuthMode={setAuthMode}
      clientCert={clientCert}
      setClientCert={setClientCert}
      clientKey={clientKey}
      setClientKey={setClientKey}
      caCert={caCert}
      setCaCert={setCaCert}
    />
  );

  return { fields, toPayload };
}
